/**
 * Jobs & Projects API client — Section 13 (Session 3.16).
 *
 * Mirrors the public surfaces exported by
 * apps/api/src/services/jobs/jobsService.ts. Jobs may be created either
 * via the Quotations win flow OR directly via `createJob` (with no
 * linked quotation).
 */

import { apiFetch } from './api';
import type { CustomerCreditStanding } from './creditStandingApi';
import type { JobStageDef } from '@jeanieiq/types';

/* ─────────────────────────── Public types ─────────────────────────── */

/**
 * A customer's account standing, carried onto a job so the person doing the
 * work sees it. Shane, 2026-09-01:
 *
 *   *"the red.. needs to feed into any project the customer with a RED flag and
 *   advise they that an issue... once an job is won and it goes to projects or
 *   engineering the customer details in the jobs / project with that customer
 *   need to be flagged to the person who is doing work for that customer that
 *   there is an issue"*
 *
 * Same shape and same source as the CRM traffic light - read from the sales
 * ledger, never stored - so it cannot mean one thing on a job and another in
 * the CRM.
 */

export interface JobPublic {
  id: string;
  reference: string;
  quotationId: string | null;
  customerId: string;
  customerName: string;
  creditStanding: CustomerCreditStanding | null;
  description: string;
  siteLocation: string | null;
  // spec-44 P3 — works description carried from the quote at Won.
  worksSummary: string;
  // Tier 2 — per-job engagement (null = use customer default).
  engagementType: string | null;
  status: string;
  stage: string;
  contractValue: string;
  targetMarginPct: string;
  actualCostToDate: string;
  forecastFinalCost: string;
  forecastMarginPct: string;
  leadEngineerId: string | null;
  leadEngineerName: string | null;
  assignedStaff: string[];
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  jobPackKey: string | null;
  riskAssessmentIds: string[];
  permitIds: string[];
  invoiceId: string | null;
  notes: string | null;
  // Customer PO card (Sales folder, Customised tier).
  customerPoNumber: string | null;
  paymentTermsDays: number;
  retentionPct: string;
  retentionMonths: number;
  createdAt: string;
  updatedAt: string;
}

export interface JobStats {
  active: number;
  contractValueTotal: string;
  atRiskCount: number;
  openVariationCount: number;
}

export interface JobListResult {
  jobs: JobPublic[];
  stats: JobStats;
}

export interface StockAllocationPublic {
  id: string;
  jobId: string;
  partId: string | null;
  partNumber: string;
  partDescription: string;
  quantityAllocated: number;
  quantityIssued: number;
  allocationStatus: string;
  unit: string;
  unitCost: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TimesheetEntryPublic {
  id: string;
  jobId: string;
  employeeId: string;
  employeeName: string;
  date: string;
  labourCategory: string;
  hoursWorked: number;
  rateApplied: string;
  costAmount: string;
  biometricConfirmed: boolean;
  gateEventId: string | null;
  notes: string | null;
  createdAt: string;
}

export interface MaterialUsageEntryPublic {
  id: string;
  jobId: string;
  partId: string;
  partNumber: string;
  quantityUsed: number;
  unitCostAtTime: string;
  costAmount: string;
  postedById: string;
  postedAt: string;
  notes: string | null;
}

export interface VariationOrderLinePublic {
  id: string;
  variationOrderId: string;
  sortOrder: number;
  description: string;
  type: string;
  quantity: number;
  unitCost: string;
  lineTotal: string;
}

export interface VariationOrderPublic {
  id: string;
  jobId: string;
  reference: string;
  description: string;
  instructedBy: string;
  instructedAt: string;
  valueExVat: string;
  approvalStatus: string;
  approvalReference: string | null;
  internalNotes: string | null;
  lines: VariationOrderLinePublic[];
  createdAt: string;
  updatedAt: string;
}

export interface JobStageHistoryPublic {
  id: string;
  jobId: string;
  previousStage: string | null;
  newStage: string;
  changedById: string;
  changedAt: string;
  notes: string | null;
}

export interface JobNotePublic {
  id: string;
  jobId: string;
  content: string;
  createdById: string;
  createdAt: string;
}

export interface JobAreaTaskPublic {
  id: string;
  jobId: string;
  areaKey: string;
  taskTitle: string;
  taskStatus: string;
  taskDueDate: string | null;
  taskAssignedTo: string | null;
  sortOrder: number;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JobDetail {
  job: JobPublic;
  stockAllocations: StockAllocationPublic[];
  timesheetEntries: TimesheetEntryPublic[];
  materialUsageEntries: MaterialUsageEntryPublic[];
  variationOrders: VariationOrderPublic[];
  stageHistory: JobStageHistoryPublic[];
  notes: JobNotePublic[];
  areaTasks: JobAreaTaskPublic[];
}

interface PLCategory {
  quoted: string;
  actual: string;
  forecast: string;
  variance: string;
}

export interface JobPL {
  contractValue: string;
  quotedCost: string;
  actualCostToDate: string;
  forecastFinalCost: string;
  byCategory: {
    materials: PLCategory;
    labour: PLCategory;
    subcontract: PLCategory;
    plantHire: PLCategory;
    total: PLCategory;
  };
}

export interface JobComplianceResult {
  operatives: Array<{
    employeeId: string;
    name: string;
    role: string;
    gateStatus: 'cleared' | 'warning' | 'grace' | 'blocked';
    reasons: string[];
  }>;
}

/* ─────────────────────────── Input payloads ─────────────────────────── */

export interface ListJobsQuery {
  engineerId?: string;
  stage?: string;
  status?: string;
  search?: string;
}

export interface ChangeStagePayload {
  newStage: string;
  /**
   * Why the job is moving on. REQUIRED — the API refuses a blank with a 422.
   *
   * Shane, 2026-08-25: *"make them put a note so they have to put a note as to
   * why it changing or moving on"*. It was optional, which let a blank reach a
   * NOT NULL column and fail (JOB-STAGE-NOTES-NULL-001); required here so a
   * screen calling this cannot compile without asking the user for a reason.
   */
  notes: string;
}

export interface AssignStaffPayload {
  leadEngineerId?: string | null;
  assignedStaff?: string[];
}

export interface PostTimesheetPayload {
  employeeId: string;
  date: string; // YYYY-MM-DD
  labourCategory:
    | 'fabrication_workshop'
    | 'site_installation'
    | 'testing_commissioning'
    | 'project_management'
    | 'design_engineering'
    | 'survey'
    | 'maintenance'
    | 'other';
  hoursWorked: number;
  rateApplied: number;
  notes?: string | null;
}

export interface VariationLinePayload {
  id?: string;
  description: string;
  type: 'parts' | 'labour' | 'misc';
  quantity: number;
  unitCost: number;
  sortOrder?: number;
}

export interface CreateVariationOrderPayload {
  description: string;
  instructedBy: string;
  instructedAt: string;
  internalNotes?: string | null;
  lines: VariationLinePayload[];
}

export interface UpdateVariationOrderPayload {
  approvalStatus?: 'verbal' | 'written' | 'disputed' | 'rejected';
  approvalReference?: string | null;
  description?: string;
  lines?: VariationLinePayload[];
}

export interface AddJobDocumentPayload {
  filename: string;
  s3Key: string;
}

export interface CreateAreaTaskPayload {
  taskTitle: string;
  taskStatus?: 'not_started' | 'in_progress' | 'complete' | 'blocked';
  taskDueDate?: string | null;
  taskAssignedTo?: string | null;
  sortOrder?: number;
}

export interface UpdateAreaTaskPayload {
  taskStatus?: 'not_started' | 'in_progress' | 'complete' | 'blocked';
  taskTitle?: string;
  taskDueDate?: string | null;
  taskAssignedTo?: string | null;
}

/* ─────────────────────────── Jobs ─────────────────────────── */

export async function fetchJobs(
  q: ListJobsQuery = {},
  signal?: AbortSignal,
): Promise<JobListResult> {
  return apiFetch<JobListResult>('/api/jobs', {
    method: 'GET',
    query: {
      engineerId: q.engineerId ?? null,
      stage: q.stage ?? null,
      status: q.status ?? null,
      search: q.search ?? null,
    },
    signal,
  });
}

export interface JobHandover {
  jobId: string;
  reference: string;
  /** "R02": the revision the customer ordered. */
  wonRevision: string | null;
  orderReference: string;
  quote: { id: string; reference: string; project: string; engineer: string; type: string };
  engineerName: string | null;
  partsSentAt: string | null;
  partsCount: number;
  boardsWithoutParts: string[];
  parts: { ready: boolean; summary: string } | null;
}

export async function fetchJobHandover(id: string, signal?: AbortSignal): Promise<JobHandover | null> {
  return apiFetch<JobHandover | null>(`/api/jobs/${encodeURIComponent(id)}/handover`, { method: 'GET', signal });
}

export async function sendJobPartsToStock(id: string): Promise<JobHandover> {
  return apiFetch<JobHandover>(`/api/jobs/${encodeURIComponent(id)}/send-parts-to-stock`, { method: 'POST' });
}

export async function fetchJob(id: string, signal?: AbortSignal): Promise<JobDetail> {
  return apiFetch<JobDetail>(`/api/jobs/${encodeURIComponent(id)}`, { method: 'GET', signal });
}

// The job's REAL Purchase Orders panel (spec-44 P2): customer PO (number +
// document revisions), supplier POs raised against this job, and the
// internal stock requisition (reserved / picked from own shelves).
export interface JobPurchaseOrdersView {
  customerPo: {
    number: string | null;
    documents: Array<{
      id: string;
      filename: string;
      revNumber: number;
      isCurrent: boolean;
      createdAt: string;
    }>;
  };
  supplierPos: Array<{
    id: string;
    poReference: string;
    supplierName: string;
    status: string;
    totalValue: number;
    lineCount: number;
    partsDelivered: number;
    partsAwaited: number;
    requiredByDate: string | null;
    createdAt: string;
  }>;
  requisition: {
    lines: Array<{
      partNumber: string;
      description: string;
      reservedQty: number;
      issuedQty: number;
      notFoundQty: number;
      unitCost: number | null;
      status: string;
    }>;
    reservedValue: number;
    issuedValue: number;
    pickingList: { documentId: string; filename: string; revNumber: number } | null;
  };
}

export async function fetchJobPurchaseOrders(
  jobId: string,
  signal?: AbortSignal,
): Promise<JobPurchaseOrdersView> {
  return apiFetch<JobPurchaseOrdersView>(
    `/api/jobs/${encodeURIComponent(jobId)}/purchase-orders`,
    { method: 'GET', signal },
  );
}

// Site-work scheduling (spec-44 P3) — the job books engineer day(s)
// straight into the Fleet Site Works planner.
export interface JobScheduledDay {
  dayRowId: string;
  date: string; // YYYY-MM-DD
  employeeId: string;
  employeeName: string;
  jobLabel: string;
  siteName: string;
  area: string;
  travelHrs: number;
  siteHrs: number;
}

export async function fetchJobSiteSchedule(
  jobId: string,
  signal?: AbortSignal,
): Promise<JobScheduledDay[]> {
  return apiFetch<JobScheduledDay[]>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-schedule`,
    { method: 'GET', signal },
  );
}

// How the booking's travel hours were resolved: manual entry, a Travel Zone
// match, a postcode-distance estimate, or nothing to go on.
export type TravelSource = 'manual' | 'zone' | 'estimate' | 'none';

export interface ScheduleJobSiteWorkResult {
  scheduled: JobScheduledDay[];
  /**
   * Days the engineer is already on OTHER work, when `onClash` was left at
   * 'report'. Non-empty means NOTHING was booked — check it before telling
   * anybody the days went in.
   */
  clashes: string[];
  /**
   * Days the engineer is on approved leave, when ignoreLeave was not set.
   * Non-empty means NOTHING was booked.
   */
  unavailable: string[];
  travelSource: TravelSource;
  // travelHrs is BOTH directions when estimated (the engineer returns too);
  // travelEachWayHrs carries the one-way leg for display.
  travelHrs: number;
  travelEachWayHrs: number | null;
}

export async function scheduleJobSiteWork(
  jobId: string,
  payload: {
    employeeId: string;
    dates: string[];
    area?: string;
    travelHrs?: number;
    siteHrs?: number;
    /**
     * What to do when the engineer already has different work that day:
     * 'report' (the default — book nothing and list them), 'replace', or
     * 'alongside', a second job on the same day, which the database has allowed
     * since 2026-09-06.
     *
     * Until then this call threw a 409 telling you to go and free the day in
     * the Site Works planner first. That advice stopped being true.
     */
    onClash?: 'report' | 'replace' | 'alongside';
    /** Book onto days they are on approved leave anyway. Default false. */
    ignoreLeave?: boolean;
  },
): Promise<ScheduleJobSiteWorkResult> {
  return apiFetch<ScheduleJobSiteWorkResult>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-schedule`,
    { method: 'POST', body: payload },
  );
}

export async function unscheduleJobSiteDay(
  jobId: string,
  dayRowId: string,
): Promise<{ cleared: boolean }> {
  return apiFetch<{ cleared: boolean }>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-schedule/${encodeURIComponent(dayRowId)}`,
    { method: 'DELETE' },
  );
}

export interface CreateJobPayload {
  customerId: string;
  description: string;
  siteLocation?: string | null;
  engagementType?: 'supply_only' | 'supply_and_site_work';
  contractValue: number;
  targetMarginPct?: number;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  notes?: string | null;
}

export async function createJob(payload: CreateJobPayload): Promise<JobPublic> {
  return apiFetch<JobPublic>('/api/jobs', { method: 'POST', body: payload });
}

// PUT /api/jobs/:id — partial update for the Sales-folder edit modals
// on the Customised tier. Every field optional; only included keys are
// written. Reference/customerId/status/stage are not writable here.
export interface UpdateJobPayload {
  description?: string;
  contractValue?: number;
  targetMarginPct?: number;
  engagementType?: 'supply_only' | 'supply_and_site_work' | null;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  customerPoNumber?: string | null;
  paymentTermsDays?: number;
  retentionPct?: number;
  retentionMonths?: number;
  notes?: string | null;
  siteLocation?: string | null;
  worksSummary?: string;
}

export async function updateJob(id: string, payload: UpdateJobPayload): Promise<JobPublic> {
  return apiFetch<JobPublic>(`/api/jobs/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: payload,
  });
}

export async function changeJobStage(
  id: string,
  payload: ChangeStagePayload,
): Promise<JobPublic> {
  return apiFetch<JobPublic>(`/api/jobs/${encodeURIComponent(id)}/stage`, {
    method: 'PUT',
    body: { stage: payload.newStage, notes: payload.notes },
  });
}

export async function assignJobStaff(
  id: string,
  payload: AssignStaffPayload,
): Promise<JobPublic> {
  return apiFetch<JobPublic>(`/api/jobs/${encodeURIComponent(id)}/staff`, {
    method: 'PUT',
    body: payload,
  });
}

export async function fetchJobCompliance(
  id: string,
  signal?: AbortSignal,
): Promise<JobComplianceResult> {
  return apiFetch<JobComplianceResult>(`/api/jobs/${encodeURIComponent(id)}/compliance`, {
    method: 'GET',
    signal,
  });
}

/* ─────────────────────────── Timesheets ─────────────────────────── */

export async function postTimesheet(
  jobId: string,
  payload: PostTimesheetPayload,
): Promise<TimesheetEntryPublic> {
  return apiFetch<TimesheetEntryPublic>(`/api/jobs/${encodeURIComponent(jobId)}/timesheet`, {
    method: 'POST',
    body: payload,
  });
}

export async function fetchTimesheet(
  jobId: string,
  signal?: AbortSignal,
): Promise<TimesheetEntryPublic[]> {
  return apiFetch<TimesheetEntryPublic[]>(`/api/jobs/${encodeURIComponent(jobId)}/timesheet`, {
    method: 'GET',
    signal,
  });
}

/* ─────────────────────────── Variations ─────────────────────────── */

export async function createVariationOrder(
  jobId: string,
  payload: CreateVariationOrderPayload,
): Promise<VariationOrderPublic> {
  return apiFetch<VariationOrderPublic>(`/api/jobs/${encodeURIComponent(jobId)}/variations`, {
    method: 'POST',
    body: payload,
  });
}

export async function updateVariationOrder(
  jobId: string,
  voId: string,
  payload: UpdateVariationOrderPayload,
): Promise<VariationOrderPublic> {
  return apiFetch<VariationOrderPublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/variations/${encodeURIComponent(voId)}`,
    { method: 'PUT', body: payload },
  );
}

export async function fetchVariationOrders(
  jobId: string,
  signal?: AbortSignal,
): Promise<VariationOrderPublic[]> {
  return apiFetch<VariationOrderPublic[]>(`/api/jobs/${encodeURIComponent(jobId)}/variations`, {
    method: 'GET',
    signal,
  });
}

/* ─────────────────────────── Documents ─────────────────────────── */

export async function addJobDocument(
  jobId: string,
  payload: AddJobDocumentPayload,
): Promise<JobNotePublic> {
  return apiFetch<JobNotePublic>(`/api/jobs/${encodeURIComponent(jobId)}/documents`, {
    method: 'POST',
    body: payload,
  });
}

/* ─────────────────────────── P&L ─────────────────────────── */

export async function fetchJobPL(jobId: string, signal?: AbortSignal): Promise<JobPL> {
  return apiFetch<JobPL>(`/api/jobs/${encodeURIComponent(jobId)}/pl`, { method: 'GET', signal });
}

/* ─────────────────────────── Area tasks ─────────────────────────── */

export async function fetchAreaTasks(
  jobId: string,
  areaKey: string,
  signal?: AbortSignal,
): Promise<JobAreaTaskPublic[]> {
  return apiFetch<JobAreaTaskPublic[]>(
    `/api/jobs/${encodeURIComponent(jobId)}/areas/${encodeURIComponent(areaKey)}/tasks`,
    { method: 'GET', signal },
  );
}

export async function createAreaTask(
  jobId: string,
  areaKey: string,
  payload: CreateAreaTaskPayload,
): Promise<JobAreaTaskPublic> {
  return apiFetch<JobAreaTaskPublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/areas/${encodeURIComponent(areaKey)}/tasks`,
    { method: 'POST', body: payload },
  );
}

export async function updateAreaTask(
  jobId: string,
  areaKey: string,
  taskId: string,
  payload: UpdateAreaTaskPayload,
): Promise<JobAreaTaskPublic> {
  return apiFetch<JobAreaTaskPublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/areas/${encodeURIComponent(areaKey)}/tasks/${encodeURIComponent(taskId)}`,
    { method: 'PUT', body: payload },
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Customised tier (Panelbuilder) — Engineers + Inbox feeds (Phase C 2026-05-23)
// ────────────────────────────────────────────────────────────────────────────

export interface EngineerSummary {
  id: string;
  name: string;
  role: string;
  jobCount: number;
  activeJobCount: number;
  contractValueTotal: string;
  atRiskCount: number;
}

export interface UnallocatedJobPublic {
  id: string;
  reference: string;
  description: string;
  customerId: string;
  customerName: string;
  contractValue: string;
  quotationId: string | null;
  quotationReference: string | null;
  createdAt: string;
  daysWaiting: number;
  creditStanding: CustomerCreditStanding | null;
}

export async function fetchEngineers(signal?: AbortSignal): Promise<EngineerSummary[]> {
  return apiFetch<EngineerSummary[]>('/api/jobs/engineers', { method: 'GET', signal });
}

export async function fetchUnallocatedJobs(signal?: AbortSignal): Promise<UnallocatedJobPublic[]> {
  return apiFetch<UnallocatedJobPublic[]>('/api/jobs/unallocated', { method: 'GET', signal });
}

/** The company's job stage flow: its sector's, else the built-in one. */
export interface JobStageFlow {
  sector: string;
  stages: JobStageDef[];
}

export async function fetchJobStageFlow(signal?: AbortSignal): Promise<JobStageFlow> {
  return apiFetch<JobStageFlow>('/api/jobs/stage-flow', { method: 'GET', signal });
}

/* ─────────── Issues after handover (ISO 9001 8.5.5 on close) ─────────── */

export type AfterHandoverKind = 'call_back' | 'defect' | 'warranty' | 'service_visit' | 'other';

export interface AfterHandoverIssue {
  id: string;
  kind: AfterHandoverKind;
  raisedOn: string;
  summary: string;
  customerFeedbackId: string | null;
  complaintSummary: string | null;
  status: 'open' | 'closed';
  workDone: string | null;
  closedAt: string | null;
}

export interface AfterHandoverView {
  jobComplete: boolean;
  issues: AfterHandoverIssue[];
  complaints: Array<{ id: string; receivedOn: string; summary: string }>;
}

export interface RaiseAfterHandoverInput {
  kind: AfterHandoverKind;
  raisedOn: string;
  summary: string;
  customerFeedbackId: string | null;
}

/** The three calls the after-handover dialog makes, against whichever jobs API the screen uses. */
export interface AfterHandoverApi {
  list: (jobId: string, signal?: AbortSignal) => Promise<AfterHandoverView>;
  raise: (jobId: string, input: RaiseAfterHandoverInput) => Promise<{ id: string }>;
  close: (jobId: string, issueId: string, workDone: string) => Promise<{ id: string }>;
}

export function afterHandoverApiAt(base: string): AfterHandoverApi {
  const at = (jobId: string): string => `${base}/${encodeURIComponent(jobId)}/after-handover`;
  return {
    list: (jobId, signal) => apiFetch<AfterHandoverView>(at(jobId), { method: 'GET', signal }),
    raise: (jobId, input) => apiFetch<{ id: string }>(at(jobId), { method: 'POST', body: input }),
    close: (jobId, issueId, workDone) =>
      apiFetch<{ id: string }>(`${at(jobId)}/${encodeURIComponent(issueId)}/close`, { method: 'PUT', body: { workDone } }),
  };
}

export const jobsAfterHandoverApi = afterHandoverApiAt('/api/jobs');

// ────────────────────────────────────────────────────────────────────────────
// Customised tier — RAMS templates + per-job instances (Phase F 2026-05-23)
// ────────────────────────────────────────────────────────────────────────────

export interface RamsTemplatePublic {
  id: string;
  name: string;
  category: string;
  icon: string;
  isStock: boolean;
  bodyJson: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface RamsInstancePublic {
  id: string;
  jobId: string;
  templateId: string | null;
  name: string;
  status: string;
  bodyJson: unknown;
  issuedAt: string | null;
  pdfS3Key: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRamsInstancePayload {
  templateId?: string | null;
  name?: string;
}

export interface UpdateRamsInstancePayload {
  name?: string;
  status?: 'draft' | 'issued' | 'superseded';
  bodyJson?: unknown;
}

export async function fetchRamsTemplates(signal?: AbortSignal): Promise<RamsTemplatePublic[]> {
  return apiFetch<RamsTemplatePublic[]>('/api/rams-templates', { method: 'GET', signal });
}

export async function fetchJobRams(
  jobId: string,
  signal?: AbortSignal,
): Promise<RamsInstancePublic[]> {
  return apiFetch<RamsInstancePublic[]>(
    `/api/jobs/${encodeURIComponent(jobId)}/rams`,
    { method: 'GET', signal },
  );
}

export async function createJobRams(
  jobId: string,
  payload: CreateRamsInstancePayload,
): Promise<RamsInstancePublic> {
  return apiFetch<RamsInstancePublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/rams`,
    { method: 'POST', body: payload },
  );
}

export async function updateJobRams(
  jobId: string,
  ramsId: string,
  payload: UpdateRamsInstancePayload,
): Promise<RamsInstancePublic> {
  return apiFetch<RamsInstancePublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/rams/${encodeURIComponent(ramsId)}`,
    { method: 'PUT', body: payload },
  );
}

export async function deleteJobRams(
  jobId: string,
  ramsId: string,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/api/jobs/${encodeURIComponent(jobId)}/rams/${encodeURIComponent(ramsId)}`,
    { method: 'DELETE' },
  );
}

// spec-44 P4 — issue the RAMS: stamped issued, rendered as a tenant-
// letterheaded PDF and filed into the travelling folder's RAMS area
// (re-issue = next revision).
export interface IssueRamsResult {
  ramsId: string;
  status: string;
  filed: { folder: string; filename: string; revNumber: number };
}

export async function issueJobRams(
  jobId: string,
  ramsId: string,
): Promise<IssueRamsResult> {
  return apiFetch<IssueRamsResult>(
    `/api/jobs/${encodeURIComponent(jobId)}/rams/${encodeURIComponent(ramsId)}/issue`,
    { method: 'POST' },
  );
}

// spec-44 P4.2 — generate the Site Visit Report / Job Sheet (DOC 90 layout)
// pre-filled from the job; files into the Site Work Sheet folder
// (re-generate = next revision).
export async function generateJobSiteWorkSheet(
  jobId: string,
): Promise<{ folder: string; filename: string; revNumber: number }> {
  return apiFetch<{ folder: string; filename: string; revNumber: number }>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-work-sheet`,
    { method: 'POST' },
  );
}

// spec-44 P4.2/P4.3 — editable site-document drafts: pre-filled from the
// job on first open, every field editable and saved; the PDF generates
// from the saved draft.
export type JobSiteDocType =
  | 'site_work_sheet'
  | 'permit_to_work'
  // spec-44 P4.4 — the seven site test sheets (site-works family; PDFs
  // file into the Site Work Sheet folder, own rev chain per sheet).
  | 'site_installation_checklist'
  | 'site_electrical_test_schedule'
  | 'site_modification_checklist'
  | 'changeover_test_schedule'
  | 'ductor_test_schedule'
  | 'site_commissioning_checklist'
  | 'tx_flash_meggar_test';

export interface JobSiteDocumentPublic {
  id: string;
  docType: JobSiteDocType;
  status: string;
  bodyJson: Record<string, string>;
  updatedAt: string;
}

export async function fetchJobSiteDoc(
  jobId: string,
  docType: JobSiteDocType,
  signal?: AbortSignal,
): Promise<JobSiteDocumentPublic> {
  return apiFetch<JobSiteDocumentPublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-doc/${docType}`,
    { method: 'GET', signal },
  );
}

export async function saveJobSiteDoc(
  jobId: string,
  docType: JobSiteDocType,
  bodyJson: Record<string, string>,
): Promise<JobSiteDocumentPublic> {
  return apiFetch<JobSiteDocumentPublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-doc/${docType}`,
    { method: 'PUT', body: { bodyJson } },
  );
}

export async function refreshJobSiteDoc(
  jobId: string,
  docType: JobSiteDocType,
): Promise<JobSiteDocumentPublic> {
  return apiFetch<JobSiteDocumentPublic>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-doc/${docType}/refresh`,
    { method: 'POST' },
  );
}

// spec-44 P5 — completion → Accounts: close-out checklist + Send to
// Accounts (completes the job and raises the draft invoice).
export interface CloseoutStamp {
  at: string;
  byName: string;
}

export interface JobCompletionView {
  stage: string;
  status: string;
  isComplete: boolean;
  canSend: boolean;
  checks: {
    siteDaysBooked: number;
    partsIssued: boolean;
    siteWorkSheetFiled: boolean;
    permitFiled: boolean;
    ramsFiled: boolean;
  };
  closeout: {
    sheetsReturned: CloseoutStamp | null;
    worksConfirmed: CloseoutStamp | null;
  };
  invoice: { id: string; reference: string; status: string } | null;
}

export type CloseoutStep = 'sheets_returned' | 'works_complete';

export async function setJobCloseout(
  jobId: string,
  step: CloseoutStep,
  confirmed: boolean,
): Promise<JobCompletionView> {
  return apiFetch<JobCompletionView>(
    `/api/jobs/${encodeURIComponent(jobId)}/closeout`,
    { method: 'POST', body: { step, confirmed } },
  );
}

export async function fetchJobCompletion(
  jobId: string,
  signal?: AbortSignal,
): Promise<JobCompletionView> {
  return apiFetch<JobCompletionView>(
    `/api/jobs/${encodeURIComponent(jobId)}/completion`,
    { method: 'GET', signal },
  );
}

export interface SendToAccountsResult {
  job: JobPublic | null;
  invoice: {
    invoiceId: string;
    invoiceReference: string;
    alreadyExists: boolean;
    lineCount: number;
  } | null;
  invoiceError: string | null;
}

export async function sendJobToAccounts(jobId: string): Promise<SendToAccountsResult> {
  return apiFetch<SendToAccountsResult>(
    `/api/jobs/${encodeURIComponent(jobId)}/send-to-accounts`,
    { method: 'POST' },
  );
}

// spec-44 P4.4 — generic generate: renders the saved draft of any site
// document to PDF and files it (site test sheets use this).
export async function generateJobSiteSheet(
  jobId: string,
  docType: JobSiteDocType,
): Promise<{ folder: string; filename: string; revNumber: number }> {
  return apiFetch<{ folder: string; filename: string; revNumber: number }>(
    `/api/jobs/${encodeURIComponent(jobId)}/site-doc/${docType}/generate`,
    { method: 'POST' },
  );
}

// spec-44 P4.3 — generate the ISOLATION / PERMIT TO WORK / CANCELLATION
// form (Doc 176 layout) pre-filled from the job.
export async function generateJobPermitToWork(
  jobId: string,
): Promise<{ folder: string; filename: string; revNumber: number }> {
  return apiFetch<{ folder: string; filename: string; revNumber: number }>(
    `/api/jobs/${encodeURIComponent(jobId)}/permit-to-work`,
    { method: 'POST' },
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Customised tier — Production Orders + Allocations (Phase G 2026-05-23)
// ────────────────────────────────────────────────────────────────────────────

export type ProductionLine = 'steel' | 'copper' | 'other';
export type ProductionOrderStatus = 'awaiting_check' | 'approved' | 'queried' | 'sent_to_stock';

export interface ProductionOrderLinePublic {
  id: string;
  productionOrderId: string;
  stockItemId: string | null;
  partName: string;
  partCode: string;
  quantity: string;
  unit: string;
  notes: string;
  sortOrder: number;
}

export interface ProductionOrderPublic {
  id: string;
  jobId: string;
  jobReference: string;
  jobDescription: string;
  customerName: string;
  /**
   * What accounts would say about this customer right now - the same numbers
   * the invoice gate refuses on, shown to the engineer prepping the order.
   * Null when the customer no longer resolves, which is not "nothing owed".
   */
  creditStanding: CustomerCreditStanding | null;
  line: ProductionLine;
  status: ProductionOrderStatus;
  submittedBy: string;
  submittedByName: string;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string;
  lines: ProductionOrderLinePublic[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductionOrderPayload {
  jobId: string;
  line: ProductionLine;
  lines: Array<{
    stockItemId?: string | null;
    partName: string;
    partCode?: string;
    quantity: number;
    unit?: string;
    notes?: string;
    sortOrder?: number;
  }>;
}

export interface ReviewProductionOrderPayload {
  decision: 'approve' | 'query';
  notes?: string;
}

export interface ProductionAllocationPublic {
  id: string;
  jobId: string;
  jobReference: string;
  jobDescription: string;
  department: string;
  weekStart: string;
  hours: string;
  assignedTo: string | null;
  notes: string;
}

export interface CreateProductionAllocationPayload {
  jobId: string;
  department: string;
  weekStart: string;
  hours: number;
  assignedTo?: string | null;
  notes?: string;
}

export async function fetchProductionOrders(
  filters: { line?: ProductionLine; status?: ProductionOrderStatus } = {},
  signal?: AbortSignal,
): Promise<ProductionOrderPublic[]> {
  const params = new URLSearchParams();
  if (filters.line) params.set('line', filters.line);
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch<ProductionOrderPublic[]>(`/api/production/orders${qs}`, { method: 'GET', signal });
}

export async function createProductionOrder(payload: CreateProductionOrderPayload): Promise<ProductionOrderPublic> {
  return apiFetch<ProductionOrderPublic>('/api/production/orders', { method: 'POST', body: payload });
}

export async function reviewProductionOrder(
  orderId: string,
  payload: ReviewProductionOrderPayload,
): Promise<ProductionOrderPublic> {
  return apiFetch<ProductionOrderPublic>(
    `/api/production/orders/${encodeURIComponent(orderId)}/review`,
    { method: 'PUT', body: payload },
  );
}

export async function deleteProductionOrder(orderId: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/production/orders/${encodeURIComponent(orderId)}`, { method: 'DELETE' });
}

export async function fetchProductionAllocations(
  filters: { weekStart?: string; department?: string; jobId?: string } = {},
  signal?: AbortSignal,
): Promise<ProductionAllocationPublic[]> {
  const params = new URLSearchParams();
  if (filters.weekStart) params.set('weekStart', filters.weekStart);
  if (filters.department) params.set('department', filters.department);
  if (filters.jobId) params.set('jobId', filters.jobId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch<ProductionAllocationPublic[]>(`/api/production/allocations${qs}`, { method: 'GET', signal });
}

export async function createProductionAllocation(
  payload: CreateProductionAllocationPayload,
): Promise<ProductionAllocationPublic> {
  return apiFetch<ProductionAllocationPublic>('/api/production/allocations', { method: 'POST', body: payload });
}

export async function deleteProductionAllocation(allocationId: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/production/allocations/${encodeURIComponent(allocationId)}`, { method: 'DELETE' });
}
